<!-- eslint-disable vue/attribute-hyphenation -->
<template>
  <div
    v-if="store.state.site"
    id="app"
    tabindex="-1"
  >
    <v-app>
      <template v-if="store.state.pages.Welcome.content.alert && route.name == 'Welcome'">
        <div
          class="pa-3 w-100 text-center bg-primary c-white"
          data-testid="alert"
        >
          <MarkdownContent :content="store.state.pages.Welcome.content.alert" />
        </div>
      </template>

      <template v-if="route.name != 'Welcome'">
        <template v-if="store.state.site.enable_provider_v2">
          <v-navigation-drawer
            @update:model-value="onProviderNavigationDrawerUpdate"
            :class="{ rail }"
            :model-value="providerNavigationDrawerModelValue"
            :rail="rail"
            rail-width="70"
          >
            <template v-if="showProviderPortalMainNavigation">
              <v-list-item class="px-4 py-3">
                <router-link
                  :aria-label="$bct('Home')"
                  :to="store.getters.defaultLandingPage()"
                >
                  <v-img
                    v-if="rail"
                    :src="$a.url('logo.small')"
                    alt=""
                    class="mx-auto"
                    data-cy="manager_logo"
                    max-width="30"
                  />

                  <v-img
                    v-else
                    :src="store.state.brand.logo.small || store.state.brand.logo.full"
                    alt=""
                    data-cy="manager_logo"
                    max-height="50"
                    max-width="140"
                  />
                </router-link>
              </v-list-item>

              <transition
                :key="route.path"
                :name="transitionName"
                mode="out-in"
              >
                <ManagerMainNav :show-account-settings-nav="showAccountSettingsNav" />
              </transition>
            </template>

            <template
              v-if="showProviderPortalMainNavigation"
              #append
            >
              <!-- Needed for the account settings options to mount -->
              <div
                id="manager-main-nav-footer-mount"
                class="w-100 flex-shrink-0"
              />

              <v-divider class="w90pc mx-auto" />

              <div class="mini-nav-toggle">
                <v-btn
                  v-if="!$vuetify.display.mobile"
                  @click="toggleMiniNav"
                  :aria-label="$bct('Expand or minimize navigation')"
                  :icon="miniNav ? 'chevron_right' : 'chevron_left'"
                  class="d-flex bg-very-light-grey"
                  color="light-black"
                  size="20"
                />
              </div>
            </template>
          </v-navigation-drawer>

          <v-app-bar
            id="top-navbar"
            elevation="0"
          >
            <v-app-bar-nav-icon
              v-if="showManagerMobileNavToggle"
              @click.stop="store.commit('setDrawer', true)"
              :aria-label="$bct('Select nav bar')"
              data-cy="select_nav_bar"
            />
            <v-spacer />
            <div class="d-flex align-center">
              <LocaleSelector />
              <AccountMenu v-if="showProviderPortalMainNavigation" />
            </div>
          </v-app-bar>
        </template>

        <v-app-bar
          v-else
          id="navbar"
          elevation="0"
        >
          <router-link
            :to="store.getters.defaultLandingPage()"
            aria-label="Home"
            class="px-3"
          >
            <v-img
              :class="[$vuetify.display.smAndDown ? 'logo-small' : 'logo-large', 'logo-padding']"
              :src="store.state.brand.logo.small || store.state.brand.logo.full"
              alt=""
              data-cy="manager_logo"
            />
          </router-link>

          <template v-if="store.state.profile">
            <template v-if="$vuetify.display.mdAndUp && route.meta.registration !== true">
              <StandardButton
                v-if="store.getters.isEmployed() && store.state.pages.Provider.enabled"
                :active="menuTabIsActive('/providers/list')"
                :class="getBtnClasses('/providers/list')"
                :color="navTabButtonColor('/providers/list')"
                :text="store.state.pages.Provider.title || 'Locations'"
                :to="{ name: 'ProviderIndex' }"
                data-cy="providers-button"
                size="x-large"
                variant="text"
                tile
              />

              <StandardButton
                v-if="showRequests"
                :active="menuTabIsActive('/providers/requests')"
                :class="getBtnClasses('/providers/requests')"
                :color="navTabButtonColor('/providers/requests')"
                :text="store.state.pages.Requests.title || 'Requests'"
                :to="{ name: 'FormRequestIndex' }"
                class="fs-15 px-6 h-100pc"
                data-cy="requests-button"
                size="x-large"
                variant="text"
                tile
              />

              <StandardButton
                v-if="store.getters.isEmployed() && store.state.pages.Interested.enabled"
                :active="menuTabIsActive('/interested')"
                :class="getBtnClasses('/interested')"
                :color="navTabButtonColor('/interested')"
                :text="store.state.pages.Interested.title || 'Interested'"
                :to="{ name: 'InterestedIndex' }"
                class="fs-15 px-6 h-100pc"
                data-cy="interested-button"
                size="x-large"
                variant="text"
                tile
              />

              <StandardButton
                v-if="store.getters.isEmployed() && store.state.pages.Students.enabled"
                :active="menuTabIsActive('/students')"
                :class="getBtnClasses('/students')"
                :color="navTabButtonColor('/students')"
                :text="store.state.pages.Students.title || 'Students'"
                :to="{ name: 'EnrollmentIndex' }"
                class="fs-15 px-6 h-100pc"
                data-cy="students-button"
                size="x-large"
                variant="text"
                tile
              />

              <StandardButton
                v-if="store.getters.isEmployed() && store.state.pages.Grants.enabled"
                :active="menuTabIsActive('/grants')"
                :class="getBtnClasses('/grants')"
                :color="navTabButtonColor('/grants')"
                :text="store.state.pages.Grants.title || 'Grants'"
                :to="{ name: 'GrantProgramIndex' }"
                class="fs-15 px-6 h-100pc"
                data-cy="grants-button"
                size="x-large"
                variant="text"
                tile
              />

              <StandardButton
                v-if="store.state.pages.Learning.enabled && store.state.profile.enable_courses"
                :active="menuTabIsActive('/courses')"
                :class="getBtnClasses('/courses')"
                :color="navTabButtonColor('/courses')"
                :text="store.state.pages.Learning.title || 'Learning'"
                :to="{ name: 'CourseIndex' }"
                class="fs-15 px-6 h-100pc"
                data-cy="courses-button"
                size="x-large"
                variant="text"
                tile
              />

              <StandardButton
                v-if="store.getters.isEmployed() && store.state.pages.Assessments.enabled"
                :active="menuTabIsActive('/assessments')"
                :class="getBtnClasses('/assessments')"
                :color="navTabButtonColor('/assessments')"
                :text="store.state.pages.Assessments.title || 'Assessments'"
                :to="{ name: 'AssessmentIndex' }"
                class="fs-15 px-6 h-100pc"
                data-cy="assessments-button"
                size="x-large"
                variant="text"
                tile
              />

              <StandardButton
                v-if="store.state.features.enable_payments"
                :active="menuTabIsActive('/billing_center')"
                :class="getBtnClasses('/billing_center')"
                :color="navTabButtonColor('/billing_center')"
                :to="{ name: 'BillingCenterIndex' }"
                class="fs-15 px-6 h-100pc"
                data-cy="billing-center-button"
                size="x-large"
                text="Billing Center"
                variant="text"
                tile
              />

              <StandardButton
                v-if="store.state.features.enable_provider_licenses"
                :active="menuTabIsActive('/licenses')"
                :class="getBtnClasses('/licenses')"
                :color="navTabButtonColor('/licenses')"
                :to="{ name: 'ProviderLicenseIndex' }"
                class="fs-15 px-6 h-100pc"
                data-cy="licenses-button"
                size="x-large"
                text="Licenses"
                variant="text"
                tile
              />
            </template>

            <v-spacer />

            <div class="d-flex align-center">
              <LocaleSelector />

              <StandardButton
                v-if="showMessages"
                :active="false"
                :aria-label="$bct('View messages')"
                :to="{ name: 'MessageIndex' }"
                class="c-black"
                color="black"
                data-cy="messages-button"
                icon="forum"
                variant="text"
              />

              <AccountMenu v-if="showProviderPortalMainNavigation" />
            </div>
          </template>

          <template v-else>
            <v-spacer />

            <LocaleSelector />
          </template>
        </v-app-bar>
      </template>

      <v-main class="bg-super-light-blue">
        <router-view v-slot="{ Component }">
          <component
            :is="Component"
            @chime="regularChime"
            @error="error"
            @replace="router.push($event)"
            @update:notifications="load"
            :notificationItems="notificationItems"
          />
        </router-view>
      </v-main>

      <SnackBar
        v-model="chime"
        :centered="true"
        :text="chimeText"
        :timeout="chimeTime"
        close-on-content-click
      />

      <ConfirmDialog ref="confirmDialog" />
      <HelpFlyout
        ref="helpFlyoutRef"
        :show-help-button="showHelpButton"
      />

      <v-bottom-navigation
        v-if="showBottomNavigation"
        class="overflow-x-auto"
        grow
      >
        <div class="d-flex align-center justify-space-evenly w-100">
          <StandardButton
            v-if="store.getters.isEmployed() && store.state.pages.Provider.enabled"
            :active="menuTabIsActive('/providers/list')"
            :text="store.state.pages.Provider.title || 'Locations'"
            :to="{ name: 'ProviderIndex' }"
            append-icon="home"
            class="px-2"
            data-cy="bottom-providers-button"
            variant="text"
          />

          <StandardButton
            v-if="showRequests"
            :active="menuTabIsActive('/providers/requests')"
            :text="store.state.pages.Requests.title || 'Requests'"
            :to="{ name: 'FormRequestIndex' }"
            append-icon="article"
            class="px-3"
            data-cy="bottom-requests-button"
            variant="text"
          />

          <StandardButton
            v-if="store.getters.isEmployed() && store.state.pages.Interested.enabled"
            :active="menuTabIsActive('/interested')"
            :text="store.state.pages.Interested.title || 'Interested'"
            :to="{ name: 'InterestedIndex' }"
            append-icon="favorite"
            class="px-3"
            data-cy="bottom-interested-button"
            variant="text"
          />

          <StandardButton
            v-if="store.getters.isEmployed() && store.state.pages.Students.enabled"
            :active="menuTabIsActive('/students')"
            :text="store.state.pages.Students.title || 'Students'"
            :to="{ name: 'EnrollmentIndex' }"
            append-icon="face"
            class="px-3"
            data-cy="bottom-students-button"
            variant="text"
          />

          <StandardButton
            v-if="store.getters.isEmployed() && store.state.pages.Grants.enabled"
            :active="menuTabIsActive('/grants')"
            :text="store.state.pages.Grants.title || 'Grants'"
            :to="{ name: 'GrantProgramIndex' }"
            append-icon="account_balance"
            class="px-3"
            data-cy="bottom-grants-button"
            variant="text"
          />

          <StandardButton
            v-if="store.state.pages.Learning.enabled"
            :active="menuTabIsActive('/courses')"
            :class="menuTabIsActive('/courses') ? 'c-primary' : ''"
            :text="store.state.pages.Learning.title || 'Learning'"
            :to="{ name: 'CourseIndex' }"
            append-icon="book"
            class="px-3"
            data-cy="bottom-courses-button"
            variant="text"
          />

          <StandardButton
            v-if="store.getters.isEmployed() && store.state.pages.Assessments.enabled"
            :active="menuTabIsActive('/assessments')"
            :text="store.state.pages.Assessments.title || 'Assessments'"
            :to="{ name: 'AssessmentIndex' }"
            append-icon="star_rate"
            class="px-2"
            data-cy="bottom-assessments-button"
            variant="text"
          />

          <StandardButton
            v-if="store.state.features.enable_payments"
            :active="menuTabIsActive('/billing_center')"
            :to="{ name: 'BillingCenterIndex' }"
            append-icon="receipt_long"
            class="px-4"
            data-cy="bottom-billing-center-button"
            text="Billing"
            variant="text"
          />

          <StandardButton
            v-if="store.state.features.enable_provider_licenses"
            :active="menuTabIsActive('/licenses')"
            :to="{ name: 'ProviderLicenseIndex' }"
            append-icon="verified"
            class="px-3"
            data-cy="bottom-licenses-button"
            text="Licenses"
            variant="text"
          />
        </div>
      </v-bottom-navigation>

      <!-- <UserSessionTimeout /> -->
    </v-app>
  </div>
</template>

<script setup>
import _ from 'lodash'; // eslint-disable-line id-length
import { provide } from 'vue';
import { useDisplay } from 'vuetify';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';

import AccountMenu from '@/manager/components/navigation/AccountMenu.vue';
import { AMPLITUDE_ENGAGEMENT_PROVIDE_KEY } from '@/shared/composables/analytics/useAmplitudeEngagement';
import ConfirmDialog from '@/shared/components/ConfirmDialog.vue';
import HelpFlyout from '@/shared/components/HelpFlyout.vue';
import LocaleSelector from '@/shared/components/LocaleSelector.vue';
import ManagerMainNav from '@/manager/components/navigation/ManagerMainNav.vue';
import MarkdownContent from '@/shared/components/MarkdownContent.vue';
import { needsMFA } from '@/shared/services/auth';
import { PROVIDE_KEYS } from '@/shared/assets/constants';
import { SEGMENT_PROVIDE_KEY } from '@/shared/composables/analytics/useSegment';
import SegmentService from '@/shared/services/analytics/SegmentService';
import SnackBar from '@/shared/components/SnackBar.vue';
import useAmplitudeEngagementInit from '@/shared/composables/analytics/useAmplitudeEngagementInit';
import useApi from '@/shared/composables/useApi';
import useChime from '@/shared/composables/useChime';
import useLanguageHelper from '@/shared/composables/useLanguageHelper';
// import UserSessionTimeout from '@/shared/components/UserSessionTimeout.vue';
import useSegmentPageViews from '@/shared/composables/analytics/useSegmentPageViews';
import useTranslator from '@/shared/composables/useTranslator';

const { $bct } = useTranslator();

provide(PROVIDE_KEYS.OPEN_HELP_FLYOUT, (document) => {
  helpFlyoutRef.value?.openFlyout(document);
});
provide(PROVIDE_KEYS.TRANSLATOR, $bct);

const store = useStore();

// Setup Amplitude Engagement (must be initialized before SegmentService)
const amplitudeApiKey = window.VUE_AMPLITUDE_API_KEY || null;
const amplitudeEngagement = useAmplitudeEngagementInit({
  apiKey: amplitudeApiKey,
  segmentAnalytics: window.analytics,
  store,
});
provide(AMPLITUDE_ENGAGEMENT_PROVIDE_KEY, amplitudeEngagement);

// Setup segment analytics with Amplitude Engagement integration
const segment = SegmentService.forProvider(window.analytics, amplitudeEngagement);
provide(SEGMENT_PROVIDE_KEY, segment);
useSegmentPageViews(segment);
useLanguageHelper();

provide('globalConfirmDialog', {
  confirm: (...args) => confirmDialog.value?.confirmWithText(...args),
});

const { api } = useApi();
const display = useDisplay();
const route = useRoute();
const router = useRouter();
const { chime, chimeText, chimeTime, error, regularChime } = useChime();

defineEmits(['click']);

const ACCOUNT_SETTINGS_ROUTE_NAMES = ['BusinessShow', 'ProviderStaffIndex', 'ManagerAccount'];

const agreements = ref([]);
const confirmDialog = ref(null);
const helpFlyoutRef = ref(null);
const miniNav = ref(null);
const transitionName = ref(null);

const providerPortalHasProfile = computed(() => {
  return store.state.profile && store.state.profile.organization_id === window.organization_id;
});

const showProviderPortalMainNavigation = computed(() => {
  return providerPortalHasProfile.value && route.meta.registration !== true;
});

const providerNavigationDrawerModelValue = computed(() => {
  if (!showProviderPortalMainNavigation.value) return false;
  return store.state.drawer;
});

const showManagerMobileNavToggle = computed(() => {
  return providerPortalHasProfile.value && display.mobile.value && route.meta.registration !== true;
});

const showHelpButton = computed(() => {
  if (!store.state.profile || !store.state.profile.id) return false;
  if (!store.state.config?.enable_help_documentation) return false;
  if (route.meta?.registration) return false;
  return true;
});

watch(
  () => store.state.profile,
  async () => {
    await loadAgreements();
  },
);

watch(
  route,
  (routeTo, routeFrom) => {
    store.commit('setAppBarBack', null);
    store.commit('setAppBarChip', null);
    if (routeTo.meta.depth > routeFrom?.meta.depth || 0) {
      transitionName.value = 'slide-left';
    } else {
      transitionName.value = 'slide-right';
    }
  },
  { immediate: true },
);

watch(
  miniNav,
  (newValue) => {
    store.commit('setMiniNav', newValue);
  },
  { immediate: true },
);

watch(
  [
    () => store.state.site?.enable_provider_v2,
    () => showProviderPortalMainNavigation.value,
    () => route.name,
    () => display.mobile.value,
  ],
  () => {
    if (!store.state.site?.enable_provider_v2) return;
    if (route.name === 'Welcome') return;
    const show = showProviderPortalMainNavigation.value;
    if (!display.mobile.value) store.commit('setDrawer', show);
  },
  { immediate: true },
);

const showBottomNavigation = computed(() => {
  return (
    store.state.profile &&
    !store.state.site.enable_provider_v2 &&
    display.smAndDown.value &&
    route.meta.registration !== true
  );
});

const rail = computed(() => {
  return store.state.miniNav && !display.mobile.value;
});

const showAccountSettingsNav = computed(() => {
  if (ACCOUNT_SETTINGS_ROUTE_NAMES.includes(route.name)) return true;
  return route.path.startsWith('/business/');
});

const showMessages = computed(() => {
  return (
    store.getters.isEmployed() &&
    store.state.pages.Messaging.enabled &&
    store.getters.hasBusinessMessagingAccess()
  );
});

const showRequests = computed(() => {
  return store.getters.isEmployed() && store.getters.hasProviderRequestableFormSchemas();
});

const agreementNotifications = computed(() => {
  return agreements.value?.filter((agreement) => {
    let orderedSignatures;

    switch (agreement.status) {
      case 'Sent':
        return true;
      case 'Partial':
        orderedSignatures = _.orderBy(agreement.signatures, 'ordinal');

        for (const signature of orderedSignatures) {
          if (signature.signed_at) continue;
          return signature.signatory_id === store.state.profile.id;
        }

        return false;
      default:
        return false;
    }
  });
});

const notificationItems = computed(() => {
  if (!store.state?.profile?.id) return {};

  return {
    agreements: agreementNotifications.value,
  };
});

function getBtnClasses(routeName) {
  if (!menuTabIsActive(routeName)) return 'c-black fs-15 px-6 h-100pc';
  return 'fs-15 px-6 h-100pc';
}

function navTabButtonColor(routePrefix) {
  return menuTabIsActive(routePrefix) ? 'primary' : '';
}

async function load() {
  if (route.query.chime) regularChime(route.query.chime);

  if (localStorage.getItem('miniNav')) {
    try {
      miniNav.value = JSON.parse(localStorage.miniNav);
    } catch (_error) {
      localStorage.removeItem('miniNav');
    }
  } else {
    miniNav.value = false;
  }

  await loadAgreements();
}

async function loadAgreements() {
  if (!store.state?.profile?.id) return;
  if (route.meta?.registration || needsMFA(store)) return;

  const response = await api.manager.agreement.index();
  agreements.value = response?.data;
}

function menuTabIsActive(name) {
  return route.path.startsWith(name);
}

function onProviderNavigationDrawerUpdate(value) {
  if (!showProviderPortalMainNavigation.value) return;
  store.commit('setDrawer', value);
}

function toggleMiniNav() {
  miniNav.value = !miniNav.value;
  localStorage.setItem('miniNav', JSON.stringify(miniNav.value));
}

onMounted(load);
</script>

<style scoped>
.logo-small {
  min-width: 100px;
}

.logo-large {
  min-width: 140px;
  min-height: 50px;
}

.logo-padding {
  padding-left: 10px;
}

.profile-name {
  min-width: 100px;
}

@media (max-width: 600px) {
  .profile-name {
    min-width: 100px;
  }
}

.rail.v-list-item {
  display: flex;
  align-items: center;
  justify-content: center;
}
.mini-nav-toggle {
  height: 30px;
  width: 30px;
  position: absolute;
  right: -20px;
  bottom: 92px;
  opacity: 0;
  transition: opacity 0.5s ease 3s;
  z-index: 100;
}
.mini-nav-toggle:hover {
  opacity: 1;
  transition-delay: 0s;
}
</style>
